/**
 * Core Stack & Grid Layout Components
 *
 * Flexbox stack (horizontal/vertical) and responsive grid with gap control.
 * Eliminates raw `<div className="flex ...">` patterns across the app.
 *
 * @example
 *   <Stack direction="row" gap={2} align="center">
 *     <Button>One</Button><Button>Two</Button>
 *   </Stack>
 *   <Grid columns={3} gap={4}>
 *     <Card>A</Card><Card>B</Card><Card>C</Card>
 *   </Grid>
 */
import React from 'react';
import { cn } from '@/lib/utils';

// ── Stack ───────────────────────────────────────────────────────────────
export interface StackProps {
  children: React.ReactNode;
  /** Direction */
  direction?: 'row' | 'column';
  /** Gap in spacing units (1 = 0.25rem) */
  gap?: number;
  /** Align items */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  /** Justify content */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  /** Wrap */
  wrap?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** As HTML element */
  as?: 'div' | 'section' | 'nav' | 'header' | 'footer';
  className?: string;
}

const alignMap = { start: 'items-start', center: 'items-center', end: 'items-end', stretch: 'items-stretch', baseline: 'items-baseline' };
const justifyMap = { start: 'justify-start', center: 'justify-center', end: 'justify-end', between: 'justify-between', around: 'justify-around', evenly: 'justify-evenly' };

export const Stack: React.FC<StackProps> = ({
  children,
  direction = 'column',
  gap = 2,
  align = 'stretch',
  justify = 'start',
  wrap = false,
  fullWidth = false,
  as: Tag = 'div',
  className = '',
}) => (
  <Tag
    className={cn(
      'flex',
      direction === 'row' ? 'flex-row' : 'flex-col',
      alignMap[align],
      justifyMap[justify],
      wrap && 'flex-wrap',
      fullWidth && 'w-full',
      className,
    )}
    style={{ gap: `${gap * 0.25}rem` }}
  >
    {children}
  </Tag>
);

// ── Grid ────────────────────────────────────────────────────────────────
export interface GridProps {
  children: React.ReactNode;
  /** Number of columns */
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  /** Gap */
  gap?: number;
  /** Full width */
  fullWidth?: boolean;
  /** As HTML element */
  as?: 'div' | 'section';
  className?: string;
}

export const Grid: React.FC<GridProps> = ({
  children,
  columns = 3,
  gap = 4,
  fullWidth = true,
  as: Tag = 'div',
  className = '',
}) => {
  // Simple number → fixed columns
  if (typeof columns === 'number') {
    return (
      <Tag
        className={cn('grid', fullWidth && 'w-full', className)}
        style={{
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: `${gap * 0.25}rem`,
        }}
      >
        {children}
      </Tag>
    );
  }

  // Responsive breakpoints via Tailwind classes
  const { xs = 1, sm, md, lg, xl } = columns;
  const colClasses = [
    `grid-cols-${xs}`,
    sm && `sm:grid-cols-${sm}`,
    md && `md:grid-cols-${md}`,
    lg && `lg:grid-cols-${lg}`,
    xl && `xl:grid-cols-${xl}`,
  ].filter(Boolean).join(' ');

  return (
    <Tag
      className={cn('grid', colClasses, fullWidth && 'w-full', className)}
      style={{ gap: `${gap * 0.25}rem` }}
    >
      {children}
    </Tag>
  );
};

export default Stack;
